import {
  APIOrganizationUser,
  AssetType,
  getAllAssetTypes,
  OrgAccessLevel,
  PermissionAccessLevel,
  PermissionGroup,
} from '@ardoq/api-types';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import {
  Actor,
  AssetTypePermissionConfig,
  GroupActor,
  PermissionAccessLevelRestrictionPolicy,
  UserActor,
} from './types';

export const isUser = (actor: Actor): actor is UserActor => {
  return actor.type === 'user';
};

export const isGroup = (actor: Actor): actor is GroupActor => {
  return actor.type === 'group';
};

export const isContributorSpecialGroup = (actor: Actor): boolean => {
  return isGroup(actor) && actor.predefinedBasedOnRole === 'contributor';
};

export const isAllMembersSpecialGroup = (actor: Actor): boolean => {
  return isGroup(actor) && actor.predefinedBasedOnRole === 'member';
};

export const isEntireOrganizationSpecialGroup = (actor: Actor): boolean => {
  return isGroup(actor) && actor.predefinedBasedOnRole === 'everyone';
};

export const isFixed = (actor: Actor): boolean => {
  return actor.visibility === 'fixed';
};

export const isHidden = (actor: Actor): boolean => {
  return actor.visibility === 'hidden';
};

export const isVisible = (actor: Actor): boolean => {
  return actor.visibility === 'visible';
};

const getUserRestrictionPolicy = (
  user: APIOrganizationUser
): PermissionAccessLevelRestrictionPolicy | undefined => {
  switch (user.role) {
    case OrgAccessLevel.READER:
      return {
        allowOnly: new Set([PermissionAccessLevel.READ, null]),
        name: 'reader-role',
      };
    case OrgAccessLevel.CONTRIBUTOR:
      return {
        allowOnly: new Set([PermissionAccessLevel.READ, null]),
        name: 'contributor-role',
      };
    default:
      return undefined;
  }
};

export const fromGroup = (group: PermissionGroup): GroupActor => ({
  id: group._id,
  name: group.name,
  type: 'group',
  predefinedBasedOnRole: group.predefinedBasedOnRole ?? null,
  visibility: group.predefinedBasedOnRole ? 'fixed' : 'visible',
});

export const fromUser = (user: APIOrganizationUser): Actor => ({
  id: user._id,
  name: user.name,
  role: user.role,
  type: 'user',
  visibility: 'visible',
  restriction: getUserRestrictionPolicy(user),
});

const getGroupAssetTypePermissionConfig = (
  actor: GroupActor
): AssetTypePermissionConfig => {
  switch (actor.predefinedBasedOnRole) {
    case 'contributor':
      return {
        base: [PermissionAccessLevel.READ, null],
        [AssetType.WORKSPACE]: null /* Cannot assign any values */,
        [AssetType.SCENARIO]: null /* Cannot assign any values */,
        [AssetType.TRAVERSAL]: null /* Cannot assign any values */,
      };
    case 'member':
    case 'everyone':
      return {
        base: [PermissionAccessLevel.EDIT, PermissionAccessLevel.READ, null],
      };
    case null:
      return {
        base: [
          PermissionAccessLevel.ADMIN,
          PermissionAccessLevel.EDIT,
          PermissionAccessLevel.READ,
          null,
        ],
      };
    default:
      return exhaustiveCheck(actor.predefinedBasedOnRole);
  }
};

const getUserAssetTypePermissionConfig = (
  actor: UserActor
): AssetTypePermissionConfig => {
  if (actor.role === OrgAccessLevel.CONTRIBUTOR) {
    return {
      base: [PermissionAccessLevel.READ, null],
      [AssetType.WORKSPACE]: null /* Cannot assign any values */,
      [AssetType.SCENARIO]: null /* Cannot assign any values */,
      [AssetType.TRAVERSAL]: null /* Cannot assign any values */,
    };
  }
  if (actor.role === OrgAccessLevel.READER) {
    return {
      base: [PermissionAccessLevel.READ, null],
    };
  }
  return {
    base: [
      PermissionAccessLevel.ADMIN,
      PermissionAccessLevel.EDIT,
      PermissionAccessLevel.READ,
      null,
    ],
  };
};

export const getAssetTypePermissionConfig = (
  actor: Actor
): AssetTypePermissionConfig => {
  if (isGroup(actor)) {
    return getGroupAssetTypePermissionConfig(actor);
  }
  return getUserAssetTypePermissionConfig(actor);
};

export const getAllowedPermissionValues = (
  actor: Actor,
  assetType: AssetType
): Array<PermissionAccessLevel | null> | null => {
  const config = getAssetTypePermissionConfig(actor);

  const assetConfig = config[assetType];
  if (assetConfig === null) {
    return null; // No permissions can be assigned for this asset type
  }

  return assetConfig || config.base;
};

export const mapAssetTypePermissions = <PermissionOptionT>(
  actor: Actor,
  transformFn: (
    permission: Array<PermissionAccessLevel | null> | null,
    assetType: AssetType,
    actor: Actor
  ) => PermissionOptionT
): Record<AssetType, PermissionOptionT> => {
  const { base, ...assetConfigs } = getAssetTypePermissionConfig(actor);

  const perAssetPermissions = getAllAssetTypes().map(assetType => {
    const assetPermissions = assetConfigs[assetType];

    // Distinguish between undefined and null, the former falls back to base permissions,
    // while the latter means no permissions can be assigned for this asset type.
    if (assetPermissions === undefined) {
      return [assetType, transformFn(base, assetType, actor)];
    }
    return [assetType, transformFn(assetPermissions, assetType, actor)];
  });

  // Object.fromEntries is poorly typed, so this has to be type cast
  return Object.fromEntries(perAssetPermissions) as Record<
    AssetType,
    PermissionOptionT
  >;
};
